import { useState } from 'react';
import { Button, Drawer, Space, Grid, Badge } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;

export interface FilterConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'daterange' | 'number';
  placeholder?: string;
  options?: { label: string; value: string | number }[];
  render: () => React.ReactNode;
}

interface FilterBarProps {
  filters: FilterConfig[];
  onClear?: () => void;
  onApply?: () => void;
  activeCount?: number;
  collapseToButton?: boolean;
  collapseButtonSize?: 'large' | 'middle' | 'small';
  collapseWrap?: boolean;
  extra?: React.ReactNode;
  nowrap?: boolean;
  /** Allow right-side actions to move to a new row when the filter area is narrow. */
  wrapExtra?: boolean;
  /** Replace the text trigger with a compact advanced-filter icon for list toolbars. */
  filterButtonMode?: 'label' | 'icon' | 'hidden';
  /** Render the filter trigger after the quick-search/column controls. */
  filterButtonPosition?: 'before' | 'after';
}

export function FilterBar({
  filters,
  onClear,
  onApply,
  activeCount = 0,
  collapseToButton = false,
  collapseButtonSize = 'small',
  collapseWrap = false,
  extra,
  nowrap = false,
  wrapExtra = false,
  filterButtonMode = 'label',
  filterButtonPosition = 'before',
}: FilterBarProps) {
  const screens = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const filterTriggerSize = filterButtonMode === 'icon' ? 'middle' : collapseButtonSize;

  const filterContent = (
    <Space
      wrap={!nowrap}
      style={{
        alignItems: 'flex-end',
        ...(nowrap ? { flexWrap: 'nowrap', overflowX: 'auto', maxWidth: '100%' } : {}),
        ...(wrapExtra ? { flex: '1 1 max-content', minWidth: 0 } : {}),
      }}
    >
      {filters.map((f) => (
        <div key={f.key}>
          {f.render()}
        </div>
      ))}
      {onApply && (
        <Button
          type="primary"
          onClick={onApply}
          data-testid="filter-bar-apply-btn"
        >
          Áp dụng
        </Button>
      )}
      {onClear && activeCount > 0 && (
        <Button
          onClick={onClear}
          data-testid="filter-bar-clear-btn"
        >
          Xóa bộ lọc ({activeCount})
        </Button>
      )}
    </Space>
  );

  if (screens.md && !collapseToButton) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '8px 0', gap: 8, ...(wrapExtra ? { flexWrap: 'wrap' as const } : {}) }}>
        {filterContent}
        {extra && <div style={{ flexShrink: 0, ...(wrapExtra ? { marginLeft: 'auto' } : {}) }}>{extra}</div>}
      </div>
    );
  }

  return (
    <>
      <Space wrap={collapseWrap} style={{ maxWidth: '100%', alignItems: 'center' }}>
        {filterButtonPosition === 'before' && filterButtonMode !== 'hidden' ? (
          <Badge count={activeCount} size="small" offset={[-2, 2]}>
            <Button
              size={filterTriggerSize}
              icon={<FilterOutlined />}
              aria-label={filterButtonMode === 'icon' ? 'Bộ lọc nâng cao' : 'Lọc'}
              title={filterButtonMode === 'icon' ? 'Bộ lọc nâng cao' : undefined}
              onClick={() => setDrawerOpen(true)}
            >
              {filterButtonMode === 'label' ? 'Lọc' : null}
            </Button>
          </Badge>
        ) : null}
        {extra}
        {filterButtonPosition === 'after' && filterButtonMode !== 'hidden' ? (
          <Badge count={activeCount} size="small" offset={[-2, 2]}>
            <Button
              size={filterTriggerSize}
              icon={<FilterOutlined />}
              aria-label={filterButtonMode === 'icon' ? 'Bộ lọc nâng cao' : 'Lọc'}
              title={filterButtonMode === 'icon' ? 'Bộ lọc nâng cao' : undefined}
              onClick={() => setDrawerOpen(true)}
            >
              {filterButtonMode === 'label' ? 'Lọc' : null}
            </Button>
          </Badge>
        ) : null}
      </Space>
      <Drawer
        title="Bộ lọc"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement={screens.md ? 'right' : 'bottom'}
        width={screens.md ? 420 : undefined}
        height={screens.md ? undefined : 'auto'}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {filters.map((f) => (
            <div key={f.key}>
              <div style={{ fontSize: 12, color: '#6b6b6b', marginBottom: 4 }}>{f.label}</div>
              {f.render()}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingTop: 8 }}>
            {onClear && (
              <Button
                onClick={() => {
                  onClear();
                  setDrawerOpen(false);
                }}
                size="small"
              >
                Xóa bộ lọc {activeCount > 0 && `(${activeCount})`}
              </Button>
            )}
            {onApply && (
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  onApply();
                  setDrawerOpen(false);
                }}
              >
                Áp dụng
              </Button>
            )}
          </div>
        </Space>
      </Drawer>
    </>
  );
}
